import { centsToEuros } from '@/constants'
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react'
import { BillingProfileData, CustomFieldData } from '@jobilla/nexus-sdk'
import type { InventoryItemData } from '@jobilla/nexus-sdk'
import type { AxiosError } from 'axios'
import { AnimatePresence, motion } from 'motion/react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, SelectField, TextField } from '../../../../rapid-ui'

interface InventoryConversionDialogProps {
	open: boolean
	onClose: () => void
	onConfirm: () => void
	selectedItems: InventoryItemData[]
	totalValue: number
	totalQuantity: number
	isLoading: boolean
	error: AxiosError<{ message?: string }> | null
	customFields: CustomFieldData[]
	customFieldValues: Record<string, string>
	onCustomFieldChange: (fieldId: string, value: string) => void
	billingProfiles: BillingProfileData[]
	selectedProfileId: string | null
	onProfileChange: (profileId: string) => void
}

export function InventoryConversionDialog({
	open,
	onClose,
	onConfirm,
	selectedItems,
	totalValue,
	totalQuantity,
	isLoading,
	error,
	customFields,
	customFieldValues,
	onCustomFieldChange,
	billingProfiles,
	selectedProfileId,
	onProfileChange,
}: InventoryConversionDialogProps) {
	const { t } = useTranslation('common')

	// Selections are homogeneous (the backend rejects mixed invoiced/uninvoiced batches),
	// so the first item's isInvoiced applies to the whole selection.
	const isInvoicedBatch = selectedItems[0]?.isInvoiced ?? false

	const profileLabel = (profile: BillingProfileData) => {
		const name = profile.name || profile.email || profile.stripeCustomerId
		return profile.isPrimary ? `${name} ${t('inventory.conversion.primaryBadge')}` : name
	}

	const selectedProfile = billingProfiles.find((profile) => profile.id === selectedProfileId) ?? null

	const isMissingRequiredField = useMemo(() => {
		if (isInvoicedBatch) { return false }
		return customFields
			.filter((f) => f.isRequired)
			.some((f) => !customFieldValues[f.id]?.trim())
	}, [customFields, customFieldValues, isInvoicedBatch])

	const hasFieldValueTooLong = useMemo(() => {
		if (isInvoicedBatch) { return false }
		return Object.values(customFieldValues).some((v) => v.length > 140)
	}, [customFieldValues, isInvoicedBatch])
	return (
		<AnimatePresence>
			{open && (
				<Dialog static open={open} onClose={onClose} className="relative z-50">
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						className="fixed inset-0 bg-black/30 backdrop-blur-sm"
					/>

					<div className="fixed inset-0 flex items-center justify-center p-4">
						<DialogPanel
							as={motion.div}
							initial={{ opacity: 0, scale: 0.95 }}
							animate={{ opacity: 1, scale: 1 }}
							exit={{ opacity: 0, scale: 0.95 }}
							className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden"
						>
							<div className="p-6">
								<DialogTitle className="text-lg font-semibold text-gray-900">
									{t('inventory.conversion.title')}
								</DialogTitle>

								<div className="mt-4 space-y-4">
									<p className="text-sm text-gray-600">
										{t('inventory.conversion.description', { count: selectedItems.length })}
									</p>

									{/* Summary box */}
									<div className="bg-gray-50 rounded-lg p-4 space-y-2">
										<div className="flex justify-between text-sm">
											<span className="text-gray-500">
												{t('inventory.conversion.totalValue')}
											</span>
											<span className="font-medium text-gray-900">
												&euro;{centsToEuros(totalValue).toFixed(2)}
											</span>
										</div>
										<div className="flex justify-between text-sm">
											<span className="text-gray-500">{t('inventory.conversion.campaigns')}</span>
											<span className="font-medium text-gray-900">{totalQuantity}</span>
										</div>
									</div>

									{/* Items list */}
									{selectedItems.length > 0 && selectedItems.length <= 5 && (
										<div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-40 overflow-y-auto">
											{selectedItems.map((item) => (
												<div key={item.id} className="px-3 py-2 text-sm">
													<div className="flex justify-between">
														<span className="text-gray-600 truncate flex-1 mr-2">
															{item.description
																|| t('inventory.conversion.inventoryItem')}
														</span>
														<span className="text-gray-900 font-medium flex-shrink-0">
															&euro;{centsToEuros(item.valueCents).toFixed(2)} &times;
															{' '}
															{item.quantity}
														</span>
													</div>
												</div>
											))}
										</div>
									)}

									{!isInvoicedBatch && billingProfiles.length > 1 && (
										<SelectField<BillingProfileData>
											label={t('inventory.conversion.billingProfile')}
											hint={t('inventory.conversion.billingProfileHint')}
											options={billingProfiles}
											value={selectedProfile as BillingProfileData}
											onChange={(profile) => onProfileChange(profile.id)}
											renderOption={profileLabel}
											disabled={isLoading}
										/>
									)}

									{!isInvoicedBatch && customFields.length > 0 && (
										<div className="space-y-3">
											<div className="text-sm font-medium text-gray-700">
												{t('settings.customFields.title')}
											</div>
											{customFields.map((field) => {
												const value = customFieldValues[field.id] ?? ''
												const isEmpty = !value || value.trim() === ''
												const isTooLong = value.length > 140
												const errors = [
													...(field.isRequired && isEmpty
														? [t('settings.customFields.errors.required')]
														: []),
													...(isTooLong
														? [t('settings.customFields.errors.maxlength', { max: 140 })]
														: []),
												]
												return (
													<TextField
														key={field.id}
														label={field.name}
														value={value}
														onChange={(v) => onCustomFieldChange(field.id, v)}
														hint={field.isRequired
															? t('settings.customFields.required')
															: t('settings.customFields.optional')}
														errors={errors}
														maxLength={140}
													/>
												)
											})}
										</div>
									)}

									<div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
										<div className="flex items-start gap-2">
											<WarningIcon className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
											<div>
												<div className="font-medium text-amber-900">
													{isInvoicedBatch
														? t('inventory.conversion.directCreditWarning')
														: t('inventory.conversion.invoiceWarning')}
												</div>
												<div className="text-amber-700 mt-0.5">
													{isInvoicedBatch
														? t('inventory.conversion.directCreditDescription', {
															amount: centsToEuros(totalValue).toFixed(2),
														})
														: t('inventory.conversion.invoiceDescription', {
															amount: centsToEuros(totalValue).toFixed(2),
														})}
												</div>
											</div>
										</div>
									</div>

									{error && (
										<div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm">
											<div className="font-medium text-red-900">
												{t('inventory.conversion.errorConvert')}
											</div>
											<div className="text-red-700 mt-0.5">
												{error?.response?.data?.message || error.message
													|| t('common.tryAgain')}
											</div>
										</div>
									)}
								</div>
							</div>

							<div className="bg-gray-50 px-6 py-4 flex justify-end gap-3">
								<Button type="subtle" onClick={onClose} disabled={isLoading}>
									{t('common.cancel')}
								</Button>
								<Button
									type="committal"
									onClick={onConfirm}
									isLoading={isLoading}
									disabled={isMissingRequiredField || hasFieldValueTooLong}
								>
									{isLoading
										? t('inventory.conversion.converting')
										: isInvoicedBatch
										? t('inventory.conversion.convertToCredits')
										: t('inventory.conversion.convertAndInvoice')}
								</Button>
							</div>
						</DialogPanel>
					</div>
				</Dialog>
			)}
		</AnimatePresence>
	)
}

function WarningIcon({ className }: { className?: string }) {
	return (
		<svg className={className} viewBox="0 0 20 20" fill="currentColor">
			<path
				fillRule="evenodd"
				d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
				clipRule="evenodd"
			/>
		</svg>
	)
}
